import { UseDisclosureReturn } from "@chakra-ui/react"
import { LinkTo } from "@pathwright/ux/src/components/link/Link"
import { RefObject } from "react"
import {
  CoverPageCategoryFragment,
  CoverPageContributorFragment,
  CoverPageOfferFragment,
  CoverPageOfferableCohortsQuery,
  CoverPageResourceFragment,
  MeFragment,
  UpdateCoverPageResourceMutationVariables
} from "@pathwright/graphql-client/src/generated"

export type CoverPageOfferableCohort = NonNullable<
  NonNullable<CoverPageOfferableCohortsQuery["groups"]>["edges"][number]["node"]
>

/** Main cover page sections (URL hash targets and scroll refs). */
export const CoverPageHashSection = {
  Header: "header",
  Description: "description",
  Preview: "preview",
  Offers: "offers",
  Contributors: "contributors",
  More: "more" // this section hosts the categories edit trigger button
} as const

export type CoverPageHashSectionKey =
  (typeof CoverPageHashSection)[keyof typeof CoverPageHashSection]

/**
 * Design panel sections. Same as hash sections except `Categories` maps to the
 * cover page "More" block (related courses via categories).
 */
export const CoverPagePanelSection = {
  Header: CoverPageHashSection.Header,
  Description: CoverPageHashSection.Description,
  Preview: CoverPageHashSection.Preview,
  Offers: CoverPageHashSection.Offers,
  Contributors: CoverPageHashSection.Contributors,
  Categories: "categories"
} as const

export type CoverPagePanelSectionKey =
  (typeof CoverPagePanelSection)[keyof typeof CoverPagePanelSection]

/**
 * Normalize a panel or page section key to the cover page scroll target
 * (`categories` → `more`; shared keys unchanged).
 */
export function toCoverPageScrollHashKey(
  sectionKey: CoverPagePanelSectionKey | CoverPageHashSectionKey
): CoverPageHashSectionKey {
  return sectionKey === CoverPagePanelSection.Categories
    ? CoverPageHashSection.More
    : sectionKey
}

export type CoverPageUpdateResourceDetailsInput = Omit<
  UpdateCoverPageResourceMutationVariables,
  | "id"
  | "name"
  | "resource_type"
  | "contributorPositioningContext"
  | "categoryPositioningContext"
> & {
  name?: string
}

export type CoverPageProps = {
  // The base route for the cover page, used to build immersive paths.
  baseCoverRoute: string
  // A resourceId should be all we need to render a CoverPage
  resourceId: number
  // Optionally scope the resource to a licensor school
  licensorSchoolId?: number | null
  // Where the cover page modal should navigate on close (Library, Path, About, etc.).
  closeTarget?: LinkTo
  // Optional close handler when the Cover Page is rendered inside a modal shell.
  onClose?: () => void
  // unified handler to open an immersive route
  getImmersiveRoutePath?: (route: ImmersiveRoute) => string | null
  // the route to open in immersive mode
  immersiveRoute?: ImmersiveRoute | null
}

export type CoverPageContainerProps = CoverPageProps

// Props: baseCoverRoute, licensorSchoolId, onClose, getImmersiveRoutePath, immersiveRoute
export type CoverPageContextType = Pick<
  CoverPageProps,
  | "baseCoverRoute"
  | "licensorSchoolId"
  | "onClose"
  | "getImmersiveRoutePath"
  | "immersiveRoute"
> & {
  // GQL
  resource: CoverPageResourceFragment
  contributors: CoverPageContributorFragment[]
  categories: CoverPageCategoryFragment[]
  offers: CoverPageOfferFragment[]
  offersLoading: boolean
  moreResources: CoverPageResourceFragment[]

  // Refs
  offersSectionRef: RefObject<HTMLDivElement>
  previewSectionRef: RefObject<HTMLDivElement>
  descriptionSectionRef: RefObject<HTMLDivElement>
  contributorSectionRef: RefObject<HTMLDivElement>
  moreSectionRef: RefObject<HTMLDivElement>
  scrollContainerRef: RefObject<HTMLDivElement>
  headerTriggerRef: RefObject<HTMLDivElement>

  // State
  offerId: number | null
  setOfferId: (offerId: number | null) => void
  offerConversionIntentId: number | null
  setOfferConversionIntentId: (offerId: number | null) => void
  videoFocused: boolean
  setVideoFocused: (videoFocused: boolean) => void
  backgroundVideoMuted: boolean
  setBackgroundVideoMuted: (backgroundVideoMuted: boolean) => void
  headerCollapsed: boolean
  setHeaderCollapsed: (collapsed: boolean) => void
  showVideoMuteButton: boolean
  setShowVideoMuteButton: (show: boolean) => void
  loadingNextCoverPage: boolean
  setLoadingNextCoverPage: (loading: boolean) => void
  /** Sticky modal close destination (Library, Path, About, etc.). */
  closeTarget: string
  isLoadingImmersiveRoute: boolean
  setIsLoadingImmersiveRoute: (loading: boolean) => void

  // Context
  me?: MeFragment

  // Handlers
  refetchCoverPageResource: () => Promise<unknown>
  refetchCoverPageOffers: () => Promise<unknown>
  updateCoverPageOfferPositions: (
    order: Array<{ id: number; position: string }> | null
  ) => void
  updateCoverPageResourceDetails: (
    details: CoverPageUpdateResourceDetailsInput
  ) => Promise<unknown>
  scrollToSection: (section?: string | null) => void
  designPanelDisclosure: UseDisclosureReturn

  // Computed
  inviteToken: string | null
  offer: CoverPageOfferFragment | null
  canPreview: boolean
  /** Always the root cover page URL — used by immersive "back to cover" UI only. */
  immersiveBackTo: string
  queriesAreLoading: boolean
  previewNavItems: Array<{ source_id: number | "path" }>
  immersiveActive?: boolean
  isMobile?: boolean
  /** True when the design panel is open. */
  isDesignMode: boolean
}

export type ImmersiveRoute = {
  name:
    | "license"
    | "register"
    | "trailer"
    | "contributors"
    | "contributor"
    | "previews"
    | "preview"
    | "offers"
    | "offer"
    | "overview"
    | "more"
  id?: string | number
}
